import React, { useState, useEffect } from 'react';
import {
  IonContent,
  IonButton,
  IonGrid,
  IonRow,
  IonCol,
  IonItem,
  IonLabel,
  IonToggle,
} from '@ionic/react';
import { LinkModel, LinkType, LinkDestination } from '../../../types/link';
import { LinkFormBase } from './LinkFormBase';
import styles from '../Dashboard.module.css';
import { LinksService } from '../../../api';

interface EditLinkFormProps {
  link: LinkModel;
  onSubmit: (link: Partial<LinkModel>, destination: Partial<LinkDestination>, file: File | null) => Promise<void>;
  onCancel: () => void;
}

export const EditLinkForm: React.FC<EditLinkFormProps> = ({ link, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState<Partial<LinkModel>>({
    ...link,
  });
  const [destination, setDestination] = useState<Partial<LinkDestination>>({});
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // In a real implementation, we would fetch the current destination
    // from the API as it's not part of the link model directly
    fetchLinkDestination();
  }, [link.id]);

  const fetchLinkDestination = async () => {
    try {
      const data = await LinksService.getLinkDestination(link.id);
      setDestination(data);
    } catch (error) {
      console.error('Error fetching link destination:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Ensure name is set to URL if it's a redirect and name is empty
      const finalFormData = { ...formData };
      if (formData.type === LinkType.Redirect && destination.url && !formData.name) {
        finalFormData.name = destination.url;
      }
      
      await onSubmit(finalFormData, destination, selectedFile);
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setLoading(false);
    }
  };

  const isFormValid = () => {
    if (loading) return false;
    if (!formData.name) return false;
    
    switch (formData.type) {
      case LinkType.File:
        // If we're editing and not changing the file, the destination should be valid
        return !!selectedFile || (destination && destination.url);
      case LinkType.Redirect:
        return !!destination.url;
      case LinkType.Collection:
        return true; // Will need additional validation when implemented
      default:
        return false;
    }
  };

  return (
    <IonContent>
      <form onSubmit={handleSubmit}>
        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <h2>Edit Link</h2>
          </div>
          
          <div className={styles.cardContent}>
            <LinkFormBase
              initialData={formData}
              initialDestination={destination}
              onFormDataChange={setFormData}
              onDestinationChange={setDestination}
              onFileChange={setSelectedFile}
              isEdit={true}
            />
            
            {/* Edit-specific fields */}
            <IonItem>
              <IonLabel>Archived</IonLabel>
              <IonToggle
                checked={formData.archived}
                onIonChange={e => setFormData(prev => ({ ...prev, archived: e.detail.checked }))}
              />
            </IonItem>
          </div>

          <div className={styles.cardFooter}>
            <IonGrid>
              <IonRow>
                <IonCol>
                  <IonButton
                    expand="block"
                    type="submit"
                    disabled={!isFormValid()}
                  >
                    {loading ? 'Saving...' : 'Save Changes'}
                  </IonButton>
                </IonCol>
                <IonCol>
                  <IonButton
                    expand="block"
                    fill="clear"
                    onClick={onCancel}
                  >
                    Cancel
                  </IonButton>
                </IonCol>
              </IonRow>
            </IonGrid>
          </div>
        </div>
      </form>
    </IonContent>
  );
}; 